/**
 * Orb and rings — the only place in the app that paints to canvas.
 *
 * Correctness rule from the handoff: the number must be right even if no frame
 * ever renders. State is therefore seeded with the final value and the count-up
 * only *replays* it; a fallback timer restores the final value if the first
 * `requestAnimationFrame` never arrives (background tab, offscreen, print).
 */
import React from 'react';
import { useT } from '../i18n/useT';
import { c, f, scoreColor } from './tokens';
import { Meta, usePrefersReducedMotion } from './primitives';

const COUNT_MS = 1500;
const FALLBACK_MS = 1700;
/** Particles idle out after this long — an infinite rAF loop on a static screen is waste. */
const SPIN_MS = 9000;

function useCountUp(target: number | null, enabled: boolean): number {
    // Seeded with the final value: if nothing below ever runs, the digit is still true.
    const [n, setN] = React.useState(target ?? 0);
    const reduced = usePrefersReducedMotion();

    React.useEffect(() => {
        if (target == null) return undefined;
        if (!enabled || reduced || typeof requestAnimationFrame === 'undefined') {
            setN(target);
            return undefined;
        }

        let raf = 0;
        let start = 0;
        let settled = false;
        const ease = (t: number) => 1 - (1 - t) ** 3;

        const tick = (now: number) => {
            if (!start) start = now;
            const e = Math.min(1, (now - start) / COUNT_MS);

            setN(target * ease(e));
            if (e < 1) raf = requestAnimationFrame(tick);
            else settled = true;
        };

        setN(0);
        raf = requestAnimationFrame(tick);

        const fb = setTimeout(() => {
            if (!settled) {
                cancelAnimationFrame(raf);
                setN(target);
            }
        }, FALLBACK_MS);

        return () => {
            clearTimeout(fb);
            cancelAnimationFrame(raf);
        };
    }, [target, enabled, reduced]);

    return n;
}

/** Particle sphere. Skipped entirely under reduced-motion — the orb still reads fine. */
function useParticles(ref: React.RefObject<HTMLCanvasElement>, col: string, size: number, count: number) {
    const reduced = usePrefersReducedMotion();

    React.useEffect(() => {
        const cnv = ref.current;

        if (!cnv || reduced) return undefined;

        const inner = size - 20;
        const dpr = window.devicePixelRatio || 1;

        cnv.width = inner * dpr;
        cnv.height = inner * dpr;
        cnv.style.width = `${inner}px`;
        cnv.style.height = `${inner}px`;

        const ctx = cnv.getContext('2d');

        if (!ctx) return undefined;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        const hex = col.startsWith('#') ? col : '#8a8580';
        const t = {
            r: parseInt(hex.slice(1, 3), 16),
            g: parseInt(hex.slice(3, 5), 16),
            b: parseInt(hex.slice(5, 7), 16),
        };

        // One pre-rendered sprite beats a per-particle gradient by an order of magnitude.
        const sp = document.createElement('canvas');

        sp.width = 64;
        sp.height = 64;
        const sc = sp.getContext('2d');

        if (!sc) return undefined;
        const g = sc.createRadialGradient(32, 32, 0, 32, 32, 32);

        g.addColorStop(0, 'rgba(255,255,255,1)');
        g.addColorStop(0.18, `rgba(${t.r},${t.g},${t.b},0.95)`);
        g.addColorStop(0.45, `rgba(${t.r},${t.g},${t.b},0.32)`);
        g.addColorStop(1, `rgba(${t.r},${t.g},${t.b},0)`);
        sc.fillStyle = g;
        sc.fillRect(0, 0, 64, 64);

        const rad = inner / 2;
        const faint = count <= 60;
        const parts = Array.from({ length: count }, () => ({
            th: Math.random() * Math.PI * 2,
            phi: Math.acos(2 * Math.random() - 1),
            rN: 0.55 + Math.sqrt(Math.random()) * 0.5,
            sp: (0.00012 + Math.random() * 0.00026) * (Math.random() < 0.92 ? 1 : -1),
            bs: 0.5 + Math.random() * 1.6,
            tw: 0.0009 + Math.random() * 0.0022,
            tp: Math.random() * Math.PI * 2,
        }));

        const cx = inner / 2;
        const cy = inner / 2;
        let last = performance.now();
        const t0 = last;
        let raf = 0;

        const tick = (now: number) => {
            const dt = Math.min(50, now - last);

            last = now;
            ctx.clearRect(0, 0, inner, inner);
            ctx.globalCompositeOperation = 'lighter';
            parts.forEach((p) => {
                p.th += p.sp * dt;
                const sPhi = Math.sin(p.phi);
                const x3 = p.rN * rad * sPhi * Math.cos(p.th);
                const y3 = p.rN * rad * Math.cos(p.phi);
                const z3 = p.rN * rad * sPhi * Math.sin(p.th);
                const depth = (z3 + rad) / (2 * rad);
                const sMul = 0.45 + depth * 1.15;
                const aMul = (faint ? 0.1 : 0.18) + depth * (faint ? 0.4 : 0.9);

                ctx.globalAlpha = Math.max(0, Math.min(1, aMul * (0.55 + 0.45 * Math.sin(now * p.tw + p.tp))));
                const ds = p.bs * sMul * 6;

                ctx.drawImage(sp, cx + x3 - ds / 2, cy + y3 - ds / 2, ds, ds);
            });
            ctx.globalAlpha = 1;
            if (now - t0 < SPIN_MS) raf = requestAnimationFrame(tick);
        };

        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [ref, col, size, count, reduced]);
}

export const Orb: React.FC<{ score?: number | null; size?: number; label?: string; empty?: boolean }> = ({
    score = null,
    size = 214,
    label,
    empty,
}) => {
    const t = useT();
    const z = scoreColor(empty ? null : score);
    const col = empty ? 'rgba(232,230,224,0.30)' : z.hex;
    const cv = React.useRef<HTMLCanvasElement>(null);
    const n = useCountUp(empty ? null : score, !empty);
    let particles = 140;

    if (empty) particles = 44;
    else if (size < 160) particles = 70;

    useParticles(cv, col, size, particles);

    const ring = size - 8;
    const r = (ring - 4) / 2;
    const cc = 2 * Math.PI * r;

    return (
        <div style={{ position: 'relative', width: size, height: size, margin: '0 auto' }}>
            <div
                style={{
                    position: 'absolute',
                    inset: -22,
                    background: `radial-gradient(circle, ${col}${empty ? '14' : '2e'} 0%, ${col}10 32%, transparent 62%)`,
                    filter: 'blur(6px)',
                }}
            />
            <div
                style={{
                    position: 'absolute',
                    inset: 6,
                    borderRadius: '50%',
                    background: `radial-gradient(circle at 35% 28%, ${col}18, transparent 55%), radial-gradient(circle at 50% 50%, rgba(0,0,0,0.55), rgba(0,0,0,0.85))`,
                    boxShadow: `inset 0 0 60px rgba(0,0,0,0.6), 0 0 44px ${col}18`,
                }}
            />
            <canvas ref={cv} style={{ position: 'absolute', top: 10, left: 10, pointerEvents: 'none' }} />
            <svg
                width={ring}
                height={ring}
                style={{ position: 'absolute', top: 4, left: 4, transform: 'rotate(-90deg)' }}
            >
                <circle cx={ring / 2} cy={ring / 2} r={r} stroke="rgba(255,255,255,0.05)" strokeWidth="2" fill="none" />
                {!empty && score != null && (
                    <circle
                        cx={ring / 2}
                        cy={ring / 2}
                        r={r}
                        stroke={col}
                        strokeWidth="2"
                        fill="none"
                        strokeLinecap="round"
                        strokeDasharray={cc}
                        strokeDashoffset={cc - (n / 100) * cc}
                        style={{ filter: `drop-shadow(0 0 4px ${col}88)` }}
                    />
                )}
            </svg>
            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                }}
            >
                {/* The label scales with the circle and never wraps: at 116px
                    a fixed 9px "ІНДЕКС РОЗВИТКУ" is wider than the orb and
                    spills over the ring. `maxWidth` is the backstop for any
                    language that still runs long. */}
                <Meta
                    style={{
                        fontSize: Math.max(6.5, Math.min(9, size * 0.046)),
                        letterSpacing: size < 150 ? '0.1em' : '0.15em',
                        color: c.muted,
                        whiteSpace: 'nowrap',
                        maxWidth: size * 0.78,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {label || t('app.orb.label')}
                </Meta>
                {empty || score == null ? (
                    <div
                        style={{
                            fontFamily: f.mono,
                            fontSize: size * 0.2,
                            fontWeight: 200,
                            color: 'rgba(232,230,224,0.22)',
                            lineHeight: 1,
                            marginTop: 6,
                        }}
                    >
                        –
                    </div>
                ) : (
                    <>
                        <div
                            style={{
                                fontFamily: f.mono,
                                fontSize: size * 0.33,
                                fontWeight: 200,
                                letterSpacing: '-0.045em',
                                lineHeight: 1,
                                color: c.textPri,
                            }}
                        >
                            {Math.round(n)}
                        </div>
                        <Meta style={{ color: col, fontSize: 11, letterSpacing: '0.16em', marginTop: 3 }}>
                            {t(z.key)}
                        </Meta>
                    </>
                )}
            </div>
        </div>
    );
};

export const Ring: React.FC<{
    score?: number | null;
    size?: number;
    sw?: number;
    children?: React.ReactNode;
    neutral?: boolean;
}> = ({ score = null, size = 44, sw = 3, children, neutral }) => {
    const z = scoreColor(neutral ? null : score);
    const r = (size - sw) / 2;
    const cc = 2 * Math.PI * r;
    const col = neutral ? 'rgba(232,230,224,0.22)' : z.hex;

    return (
        <div style={{ position: 'relative', width: size, height: size, flex: '0 0 auto' }}>
            <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
                <circle cx={size / 2} cy={size / 2} r={r} stroke={c.track} strokeWidth={sw} fill="none" />
                {score != null && !neutral && (
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={r}
                        stroke={col}
                        strokeWidth={sw}
                        fill="none"
                        strokeLinecap="round"
                        strokeDasharray={cc}
                        strokeDashoffset={cc - (score / 100) * cc}
                        style={{ filter: `drop-shadow(0 0 4px ${col}88)` }}
                    />
                )}
            </svg>
            <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>{children}</div>
        </div>
    );
};

export const BigRing: React.FC<{
    score: number | null;
    size?: number;
    sw?: number;
    label: string;
    neutral?: boolean;
}> = ({ score, size = 196, sw = 9, label, neutral }) => {
    const t = useT();
    const z = scoreColor(neutral ? null : score);
    const col = neutral ? 'rgba(232,230,224,0.24)' : z.hex;
    const r = (size - sw) / 2;
    const cc = 2 * Math.PI * r;

    return (
        <div style={{ position: 'relative', width: size, height: size, margin: '0 auto' }}>
            {!neutral && (
                <div
                    style={{
                        position: 'absolute',
                        inset: -14,
                        borderRadius: '50%',
                        background: `radial-gradient(circle, ${col}1c 0%, transparent 68%)`,
                    }}
                />
            )}
            <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', position: 'relative' }}>
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    stroke="rgba(255,255,255,0.06)"
                    strokeWidth={sw}
                    fill="none"
                />
                {score != null && !neutral && (
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={r}
                        stroke={col}
                        strokeWidth={sw}
                        fill="none"
                        strokeLinecap="round"
                        strokeDasharray={cc}
                        strokeDashoffset={cc - (score / 100) * cc}
                        style={{ filter: `drop-shadow(0 0 7px ${col}55)` }}
                    />
                )}
            </svg>
            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                }}
            >
                <Meta style={{ fontSize: 10.5, letterSpacing: '0.2em' }}>{label}</Meta>
                <div
                    style={{
                        fontFamily: f.mono,
                        fontSize: size * 0.29,
                        fontWeight: 200,
                        letterSpacing: '-0.045em',
                        lineHeight: 1,
                        color: c.textPri,
                    }}
                >
                    {score ?? '–'}
                </div>
                <Meta style={{ color: col, fontSize: 10.5, letterSpacing: '0.16em' }}>{t(z.key)}</Meta>
            </div>
        </div>
    );
};
